import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { 
  ArrowLeft, 
  Download, 
  Save, 
  FileText, 
  RotateCcw, 
  Wand2, 
  Expand, 
  Shrink, 
  RefreshCw, 
  Languages, 
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Quote,
  Link,
  Type,
  Palette,
  Undo,
  Redo,
  Copy,
  Scissors,
  Clipboard,
  X
} from 'lucide-react'
import { 
  countWords, 
  countCharacters, 
  calculateReadingTime, 
  generateDocumentStats,
  autoSave,
  exportDocument
} from './writingUtils'
import linkupService from '../../services/linkupService'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import { useSubscription } from '../../hooks/useSubscription'

const WritingEditorPage = ({ 
  title, 
  content, 
  onTitleUpdate, 
  onContentUpdate, 
  onAIAction, 
  onBack, 
  llmService,
  documentId,
  onSave
}) => {
  const [documentTitle, setDocumentTitle] = useState(title || '')
  const [documentContent, setDocumentContent] = useState(content || '')
  const [selectedText, setSelectedText] = useState('')
  const [showAISidebar, setShowAISidebar] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingAction, setProcessingAction] = useState('')
  const [lastSaved, setLastSaved] = useState(null)
  const [showCitationModal, setShowCitationModal] = useState(false)
  const [citationResults, setCitationResults] = useState([])
  const [isLoadingCitations, setIsLoadingCitations] = useState(false)
  const [citationQuery, setCitationQuery] = useState('')
  const [citationCounter, setCitationCounter] = useState(0)
  const [citationMap, setCitationMap] = useState(new Map())
  
  // Credit warning and subscription hooks
  const { checkAndWarnCredits } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  const [documentStats, setDocumentStats] = useState({
    words: 0,
    characters: 0,
    readingTime: 0,
    pages: 1
  })
  const [aiActionSuccess, setAiActionSuccess] = useState(null)
  const [fontSize, setFontSize] = useState(12)
  const [fontFamily, setFontFamily] = useState('Georgia')
  const [textAlign, setTextAlign] = useState('left')
  const [selectedHeading, setSelectedHeading] = useState('normal')
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [fontSizeHistory, setFontSizeHistory] = useState([12])
  const [htmlUndoStack, setHtmlUndoStack] = useState([])
  const [htmlRedoStack, setHtmlRedoStack] = useState([])

  const [isSaving, setIsSaving] = useState(false)
  
  const editorRef = useRef(null)
  const autoSaveTimeoutRef = useRef(null)
  const undoTimeoutRef = useRef(null)
  const lastUndoContentRef = useRef('')
  
  // Convert markdown and plain text to HTML for rich text display with proper styling
  const processText = (text) => {
    if (!text) return ''
    
    // Convert markdown formatting to HTML with proper styling
    let html = text
      // Headers with proper styling: ### -> <h3>, ## -> <h2>, # -> <h1>
      .replace(/^###### (.*$)/gim, `<h6 style="font-size: ${Math.max(fontSize * 0.67, 8)}pt; font-weight: bold; margin: 1.33em 0;">$1</h6>`)
      .replace(/^##### (.*$)/gim, `<h5 style="font-size: ${Math.max(fontSize * 0.83, 10)}pt; font-weight: bold; margin: 1.17em 0;">$1</h5>`)
      .replace(/^#### (.*$)/gim, `<h4 style="font-size: ${fontSize}pt; font-weight: bold; margin: 1em 0;">$1</h4>`)
      .replace(/^### (.*$)/gim, `<h3 style="font-size: ${Math.max(fontSize * 1.17, 16)}pt; font-weight: bold; margin: 0.83em 0;">$1</h3>`)
      .replace(/^## (.*$)/gim, `<h2 style="font-size: ${Math.max(fontSize * 1.5, 18)}pt; font-weight: bold; margin: 0.75em 0;">$1</h2>`)
      .replace(/^# (.*$)/gim, `<h1 style="font-size: ${Math.max(fontSize * 2, 24)}pt; font-weight: bold; margin: 0.67em 0;">$1</h1>`)
      // Bold: **text** -> <strong>text</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic: *text* -> <em>text</em>
      .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>')
      // Underline: _text_ -> <u>text</u>
      .replace(/_(.*?)_/g, '<u>$1</u>')
    
    // Handle ordered lists for bibliography (numbered items)
    const lines = html.split('\n')
    let inBibliography = false
    let processedLines = []
    let olStarted = false
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      
      // Check if we're in bibliography section
      if (line.includes('## Daftar Pustaka/Sumber Referensi')) {
        inBibliography = true
        processedLines.push(line)
        continue
      }
      
      // Check if we're leaving bibliography section
      if (inBibliography && line.startsWith('## ') && !line.includes('Daftar Pustaka')) {
        if (olStarted) {
          processedLines.push('</ol>')
          olStarted = false
        }
        inBibliography = false
        processedLines.push(line)
        continue
      }
      
      // Process numbered items in bibliography
      if (inBibliography && /^\d+\. /.test(line.trim())) {
        if (!olStarted) {
          processedLines.push('<ol>')
          olStarted = true
        }
        const content = line.replace(/^\d+\. /, '')
        processedLines.push(`<li>${content}</li>`)
      } else {
        if (inBibliography && olStarted && line.trim() === '') {
          // Skip empty lines in bibliography
          continue
        } else if (inBibliography && olStarted && line.trim() !== '') {
          // End ol if we hit non-numbered content
          processedLines.push('</ol>')
          olStarted = false
          processedLines.push(line)
        } else {
          processedLines.push(line)
        }
      }
    }
    
    // Close ol if still open at end
    if (olStarted) {
      processedLines.push('</ol>')
    }
    
    html = processedLines.join('<br>')
    
    return html
  }

  // Convert HTML back to markdown/plain text
  const unprocessText = (html) => {
    if (!html) return ''
    
    // Convert HTML formatting back to markdown
    let text = html
      // Headers: <h1>text</h1> -> # text, <h2>text</h2> -> ## text, <h3>text</h3> -> ### text
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1')
      .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1')
      .replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1')
      .replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1')
      // Strong/Bold: <strong>text</strong> -> **text**
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      // Emphasis/Italic: <em>text</em> -> *text*
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      // Underline: <u>text</u> -> _text_
      .replace(/<u[^>]*>(.*?)<\/u>/gi, '_$1_')
      // Remove span elements but keep their content (for font size spans)
      .replace(/<span[^>]*>(.*?)<\/span>/gi, '$1')
    
    // Handle ordered lists - convert back to numbered format
    text = text.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, content) => {
      const items = content.match(/<li[^>]*>(.*?)<\/li>/gi)
      if (items) {
        return items.map((item, index) => {
          const cleanItem = item.replace(/<li[^>]*>(.*?)<\/li>/gi, '$1')
          return `${index + 1}. ${cleanItem}`
        }).join('\n')
      }
      return content
    })
    
    // Line breaks: <br> -> \n
    text = text.replace(/<br\s*\/?>/gi, '\n')
    
    // Remove other HTML tags
    text = text.replace(/<[^>]*>/g, '')
    
    // Decode HTML entities
    text = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
    
    return text
  }


  




  // Update content from props - only when content prop changes externally
  useEffect(() => {
    if (content !== documentContent && content !== undefined) {
      setDocumentContent(content || '')
      // Update the contentEditable div if it exists and not currently focused
      const editor = editorRef.current
      if (editor && content !== undefined && document.activeElement !== editor) {
        const processedHtml = processText(content)
        editor.innerHTML = processedHtml
        // Update both undo stacks when content changes externally
        setHtmlUndoStack([processedHtml])
        setUndoStack([content || ''])
        setHtmlRedoStack([])
        setRedoStack([])
      }
    }
  }, [content, fontSize])

  // Initialize editor content on mount and when fontSize changes
  useEffect(() => {
    const editor = editorRef.current
    if (editor && documentContent && document.activeElement !== editor) {
      const processedHtml = processText(documentContent)
      editor.innerHTML = processedHtml
      // Initialize both undo stacks with initial content only if they're empty
      if (htmlUndoStack.length === 0) {
        setHtmlUndoStack([processedHtml])
        setUndoStack([documentContent])
      }
    }
  }, [fontSize]) // Re-run when fontSize changes to update heading styles

  // Initialize editor content when documentContent is first loaded
  useEffect(() => {
    const editor = editorRef.current
    if (editor && documentContent && htmlUndoStack.length === 0) {
      const processedHtml = processText(documentContent)
      editor.innerHTML = processedHtml
      // Initialize both undo stacks with initial content
      setHtmlUndoStack([processedHtml])
      setUndoStack([documentContent])
    }
  }, [documentContent]) // Only run when documentContent is initially loaded

  // Update title from props
  useEffect(() => {
    if (title !== documentTitle) {
      setDocumentTitle(title || '')
    }
  }, [title])

  // Update document stats
  useEffect(() => {
    const stats = generateDocumentStats(documentContent)
    setDocumentStats(stats)
  }, [documentContent])

  // Auto-save functionality
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }
    
    const autoSaveKey = documentId ? `writing_editor_${documentId}` : `writing_editor_${Date.now()}`
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave(autoSaveKey, {
        title: documentTitle,
        content: documentContent
      })
      setLastSaved(new Date())
    }, 2000)
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current)
      }
    }
  }, [documentTitle, documentContent, documentId])

  // Enhanced content update handler with proper undo/redo support for all formatting
  const handleContentChange = useCallback((newContent, skipHistory = false) => {
    if (!skipHistory && newContent !== documentContent) {
      // Add current content to undo stack (markdown format)
      setUndoStack(prev => [...prev.slice(-19), documentContent]) // Keep last 20 states
      setRedoStack([]) // Clear redo stack when new content is added
      
      // Also add current HTML content to HTML undo stack
      const editor = editorRef.current
      if (editor) {
        // Capture the current HTML state before making changes
        const currentHtml = editor.innerHTML
        setHtmlUndoStack(prev => [...prev.slice(-19), currentHtml]) // Keep last 20 states
        setHtmlRedoStack([]) // Clear HTML redo stack when new content is added
      }
    }
    setDocumentContent(newContent)
    onContentUpdate(newContent)
  }, [onContentUpdate, documentContent])

  // Enhanced undo function with proper structure restoration
  const handleUndo = useCallback(() => {
    if (htmlUndoStack.length > 1 && undoStack.length > 1) {
      // Get the previous state (second to last in stack)
      const previousHtml = htmlUndoStack[htmlUndoStack.length - 2]
      const previousContent = undoStack[undoStack.length - 2]
      
      // Add current content to redo stacks
      const editor = editorRef.current
      if (editor) {
        setHtmlRedoStack(prev => [...prev, editor.innerHTML])
      }
      setRedoStack(prev => [...prev, documentContent])
      
      // Remove the last state from undo stacks
      setHtmlUndoStack(prev => prev.slice(0, -1))
      setUndoStack(prev => prev.slice(0, -1))
      
      // Update content without adding to history
      handleContentChange(previousContent, true)
      
      // Update editor display with HTML directly to preserve all formatting and structure
      if (editor) {
        editor.innerHTML = previousHtml
        // Ensure the editor content is properly synchronized
        setTimeout(() => {
          const restoredContent = unprocessText(editor.innerHTML)
          if (restoredContent !== previousContent) {
            // If there's a mismatch, prioritize the HTML structure
            setDocumentContent(restoredContent)
            onContentUpdate(restoredContent)
          }
        }, 0)
      }
    } else if (fontSizeHistory.length > 1) {
      // Undo font size change if no content to undo
      const previousFontSize = fontSizeHistory[fontSizeHistory.length - 2]
      setFontSize(previousFontSize)
      setFontSizeHistory(prev => prev.slice(0, -1))
    }
  }, [htmlUndoStack, undoStack, documentContent, handleContentChange, fontSizeHistory, onContentUpdate])

  // Enhanced redo function with proper structure restoration
  const handleRedo = useCallback(() => {
    if (htmlRedoStack.length === 0 || redoStack.length === 0) return
    
    // Get the next state from redo stacks
    const nextHtml = htmlRedoStack[htmlRedoStack.length - 1]
    const nextContent = redoStack[redoStack.length - 1]
    
    // Remove the state from redo stacks first
    setHtmlRedoStack(prev => prev.slice(0, -1))
    setRedoStack(prev => prev.slice(0, -1))
    
    // Add current content to undo stacks
    const editor = editorRef.current
    if (editor) {
      setHtmlUndoStack(prev => [...prev, editor.innerHTML])
    }
    setUndoStack(prev => [...prev, documentContent])
    
    // Update content without adding to history
    handleContentChange(nextContent, true)
    
    // Update editor display with HTML directly to preserve all formatting and structure
    if (editor) {
      editor.innerHTML = nextHtml
      // Ensure the editor content is properly synchronized
      setTimeout(() => {
        const restoredContent = unprocessText(editor.innerHTML)
        if (restoredContent !== nextContent) {
          // If there's a mismatch, prioritize the HTML structure
          setDocumentContent(restoredContent)
          onContentUpdate(restoredContent)
        }
      }, 0)
    }
  }, [htmlRedoStack, redoStack, documentContent, handleContentChange, onContentUpdate])

  // Handle font size change with undo support
  const handleFontSizeChange = useCallback((newSize) => {
    setFontSizeHistory(prev => [...prev.slice(-19), fontSize]) // Keep last 20 states
    setFontSize(newSize)
  }, [fontSize])

  // Enhanced apply font size to selected text with proper history tracking
  const applyFontSizeToSelection = useCallback((newSize) => {
    const editor = editorRef.current
    if (!editor) return
    
    editor.focus()
    const selection = window.getSelection()
    
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0)
      const selectedText = selection.toString()
      
      if (selectedText.trim()) {
        // Create a span element with the new font size
        const span = document.createElement('span')
        span.style.fontSize = `${newSize}pt`
        
        // Extract the selected content and wrap it in the span
        const contents = range.extractContents()
        span.appendChild(contents)
        
        // Insert the wrapped content back
        range.insertNode(span)
        
        // Clear selection
        selection.removeAllRanges()
        
        // Update content with proper history tracking
        setTimeout(() => {
          const newContent = unprocessText(editor.innerHTML)
          // Always add font size changes to history as they are formatting changes
          handleContentChange(newContent, false)
        }, 0)
        
        return true // Indicate that selection was processed
      }
    }
    
    return false // No selection to process
  }, [handleContentChange])

  // Enhanced apply heading function with proper history tracking
  const applyHeading = useCallback((headingType) => {
    const editor = editorRef.current
    if (!editor) return
    
    // Save current state before making changes
    const currentHtml = editor.innerHTML
    const currentContent = documentContent
    
    editor.focus()
    const selection = window.getSelection()
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      let element = range.commonAncestorContainer
      
      // Find the text node or element to apply heading to
      if (element.nodeType === Node.TEXT_NODE) {
        element = element.parentElement
      }
      
      // Get the current line content
      const currentText = selection.toString() || element.textContent
      
      if (currentText.trim()) {
        // Remove existing heading markup
        const cleanText = currentText.replace(/^#+\s*/, '')
        
        // Create appropriate HTML element with proper styling
        let newElement
        if (headingType === 'normal') {
          newElement = document.createElement('p')
          newElement.style.fontSize = `${fontSize}pt`
          newElement.style.fontWeight = 'normal'
          newElement.style.margin = '0'
        } else {
          newElement = document.createElement(headingType)
          // Apply heading-specific styles
          switch(headingType) {
            case 'h1':
              newElement.style.fontSize = `${Math.max(fontSize * 2, 24)}pt`
              newElement.style.fontWeight = 'bold'
              newElement.style.margin = '0.67em 0'
              break
            case 'h2':
              newElement.style.fontSize = `${Math.max(fontSize * 1.5, 18)}pt`
              newElement.style.fontWeight = 'bold'
              newElement.style.margin = '0.75em 0'
              break
            case 'h3':
              newElement.style.fontSize = `${Math.max(fontSize * 1.17, 16)}pt`
              newElement.style.fontWeight = 'bold'
              newElement.style.margin = '0.83em 0'
              break
            case 'h4':
              newElement.style.fontSize = `${fontSize}pt`
              newElement.style.fontWeight = 'bold'
              newElement.style.margin = '1em 0'
              break
            case 'h5':
              newElement.style.fontSize = `${Math.max(fontSize * 0.83, 10)}pt`
              newElement.style.fontWeight = 'bold'
              newElement.style.margin = '1.17em 0'
              break
            case 'h6':
              newElement.style.fontSize = `${Math.max(fontSize * 0.67, 8)}pt`
              newElement.style.fontWeight = 'bold'
              newElement.style.margin = '1.33em 0'
              break
          }
        }
        newElement.textContent = cleanText
        
        // Replace the current element or selection
        if (selection.toString()) {
          range.deleteContents()
          range.insertNode(newElement)
        } else {
          // Replace the entire parent element if it's a heading or paragraph
          const parentElement = element.closest('h1, h2, h3, h4, h5, h6, p, div')
          if (parentElement && parentElement !== editor) {
            parentElement.parentNode.replaceChild(newElement, parentElement)
          } else {
            element.textContent = ''
            element.appendChild(newElement)
          }
        }
        
        // Update content with proper history tracking for heading changes
        setTimeout(() => {
          const newContent = unprocessText(editor.innerHTML)
          // Always add heading changes to history as they are significant structural changes
          handleContentChange(newContent, false)
        }, 0)
      }
    }
    
    setSelectedHeading(headingType)
  }, [handleContentChange, fontSize, documentContent])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Shift + A to toggle AI sidebar
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault()
        setShowAISidebar(!showAISidebar)
      }
      // Escape to close AI sidebar
      if (e.key === 'Escape' && showAISidebar) {
        setShowAISidebar(false)
      }
      // Ctrl/Cmd + Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }
      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y for redo
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'Z') || ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault()
        handleRedo()
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showAISidebar, handleUndo, handleRedo])

  // Enhanced rich text formatting function with proper history tracking
  const applyFormatting = useCallback((type) => {
    const editor = editorRef.current
    if (!editor) return
    
    editor.focus()
    
    // Use execCommand for rich text formatting
    switch (type) {
      case 'bold':
        document.execCommand('bold', false, null)
        break
      case 'italic':
        document.execCommand('italic', false, null)
        break
      case 'underline':
        document.execCommand('underline', false, null)
        break
      default:
        return
    }
    
    // Update content after formatting with proper history tracking
    setTimeout(() => {
      const newContent = unprocessText(editor.innerHTML)
      // Always add formatting changes to history as they are significant
      handleContentChange(newContent, false)
    }, 0)
  }, [handleContentChange])

  // Enhanced text insertion for contentEditable with proper history tracking
  const insertText = useCallback((text) => {
    const editor = editorRef.current
    if (!editor) return
    
    editor.focus()
    
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      range.deleteContents()
      
      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      
      // Position cursor after inserted text
      range.setStartAfter(textNode)
      range.setEndAfter(textNode)
      selection.removeAllRanges()
      selection.addRange(range)
      
      // Update content with proper history tracking
      setTimeout(() => {
        const newContent = unprocessText(editor.innerHTML)
        // Add text insertion to history as it's a deliberate action
        handleContentChange(newContent, false)
      }, 0)
    }
  }, [handleContentChange])

  // Simple clipboard operations for textarea
  const handleCopy = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    
    const start = editor.selectionStart
    const end = editor.selectionEnd
    const selectedText = editor.value.substring(start, end)
    
    if (selectedText) {
      navigator.clipboard.writeText(selectedText).catch(err => {
        console.error('Failed to copy text: ', err)
      })
    }
  }, [])

  const handleCut = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    
    const selection = window.getSelection()
    const selectedText = selection.toString()
    
    if (selectedText) {
      navigator.clipboard.writeText(selectedText).then(() => {
        const range = selection.getRangeAt(0)
        range.deleteContents()
        
        const newContent = unprocessText(editor.innerHTML)
        handleContentChange(newContent) // Keep history for paste operations
      }).catch(err => {
        console.error('Failed to cut text: ', err)
      })
    }
  }, [handleContentChange])

  const handlePaste = useCallback(async () => {
    const editor = editorRef.current
    if (!editor) return
    
    try {
      const clipboardText = await navigator.clipboard.readText()
      if (clipboardText) {
        const selection = window.getSelection()
        if (selection.rangeCount) {
          const range = selection.getRangeAt(0)
          range.deleteContents()
          const textNode = document.createTextNode(clipboardText)
          range.insertNode(textNode)
          
          range.setStartAfter(textNode)
          range.setEndAfter(textNode)
          selection.removeAllRanges()
          selection.addRange(range)
        }
        
        const newContent = unprocessText(editor.innerHTML)
        handleContentChange(newContent) // Keep history for paste operations
      }
    } catch (err) {
      console.error('Failed to paste text: ', err)
    }
  }, [handleContentChange])

  // Handle text selection for contentEditable
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      setSelectedText('')
      return
    }
    
    const text = selection.toString().trim()
    
    if (text && text.length > 0) {
      setSelectedText(text)
    } else {
      setSelectedText('')
    }
  }, [])

  // Handle title change
  const handleTitleChange = (e) => {
    const newTitle = e.target.value
    setDocumentTitle(newTitle)
    onTitleUpdate(newTitle)
  }

  // Enhanced debounced function to add content to undo stack with proper HTML preservation
  const addToUndoStackDebounced = useCallback((content) => {
    // Clear existing timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current)
    }
    
    // Set new timeout to add to undo stack after user stops typing
    undoTimeoutRef.current = setTimeout(() => {
      // Only add to undo stack if content has actually changed
      if (content !== lastUndoContentRef.current && content !== documentContent) {
        const editor = editorRef.current
        
        // Add markdown content to undo stack
        setUndoStack(prev => {
          // Don't add if it's the same as the last undo state
          const lastUndoContent = prev[prev.length - 1]
          if (lastUndoContent !== documentContent) {
            return [...prev.slice(-19), documentContent] // Keep last 20 states
          }
          return prev
        })
        
        // Add HTML content to HTML undo stack with proper structure preservation
        if (editor) {
          setHtmlUndoStack(prev => {
            const currentHtml = editor.innerHTML
            const lastHtmlContent = prev[prev.length - 1]
            // Check if HTML structure has actually changed (not just whitespace)
            const normalizedCurrent = currentHtml.replace(/\s+/g, ' ').trim()
            const normalizedLast = lastHtmlContent ? lastHtmlContent.replace(/\s+/g, ' ').trim() : ''
            
            if (normalizedCurrent !== normalizedLast) {
              return [...prev.slice(-19), currentHtml] // Keep last 20 states
            }
            return prev
          })
        }
        
        setRedoStack([]) // Clear redo stack when new content is added
        setHtmlRedoStack([]) // Clear HTML redo stack when new content is added
        lastUndoContentRef.current = documentContent
      }
    }, 1000) // Wait 1 second after user stops typing
  }, [documentContent])

  // Enhanced input handler for contentEditable with proper structure preservation
  const handleEditorInput = (e) => {
    // Get content from contentEditable div
    const newContent = unprocessText(e.target.innerHTML)
    
    // Check if this is a significant change (structure, formatting, or content changes)
    const isSignificantChange = (
      newContent.includes('\n') !== documentContent.includes('\n') || // Line break changes
      newContent.split('\n').length !== documentContent.split('\n').length || // Paragraph count changes
      Math.abs(newContent.length - documentContent.length) > 10 || // Large content changes
      // Check for heading structure changes
      (newContent.match(/^#+\s/gm) || []).length !== (documentContent.match(/^#+\s/gm) || []).length ||
      // Check for formatting changes (bold, italic, underline)
      (newContent.match(/\*\*.*?\*\*/g) || []).length !== (documentContent.match(/\*\*.*?\*\*/g) || []).length ||
      (newContent.match(/\*.*?\*/g) || []).length !== (documentContent.match(/\*.*?\*/g) || []).length ||
      (newContent.match(/_.*?_/g) || []).length !== (documentContent.match(/_.*?_/g) || []).length
    )
    
    if (isSignificantChange) {
      // For significant changes, add to history immediately to preserve structure
      handleContentChange(newContent, false)
    } else {
      // For regular typing, skip history to prevent too many undo states
      handleContentChange(newContent, true)
      // Add to undo stack with debouncing for regular typing
      addToUndoStackDebounced(newContent)
    }
  }

  // Handle manual save
  const handleManualSave = async () => {
    if (!onSave || isSaving) return
    
    setIsSaving(true)
    try {
      await onSave({
        title: documentTitle,
        content: documentContent
      })
      setLastSaved(new Date())
    } catch (error) {
      console.error('Manual save failed:', error)
    } finally {
      setIsSaving(false)
    }
  }



  // Handle citation search
  const handleCitationSearch = async () => {
    if (!selectedText.trim()) {
      alert('Silakan pilih teks terlebih dahulu')
      return
    }

    try {
      setIsLoadingCitations(true)
      setIsProcessing(true)
      setProcessingAction('citation')
      
      // Check credits before proceeding
      const canProceed = await checkAndWarnCredits('academic_search')
      if (!canProceed) {
        setIsLoadingCitations(false)
        setIsProcessing(false)
        setProcessingAction('')
        return
      }

      // Search for academic citations using linkupService
      const query = `${selectedText} academic research citation source`
      setCitationQuery(selectedText)
      
      const results = await linkupService.getScientificInfo(query)
      
      if (results && results.sources && results.sources.length > 0) {
        setCitationResults(results.sources.slice(0, 10)) // Limit to 10 results
        setShowCitationModal(true)
        
        // Consume credits after successful search
        await consumeCredits('academic_search')
      } else {
        alert('Tidak ditemukan sumber sitasi yang relevan')
      }
    } catch (error) {
      console.error('Error searching citations:', error)
      alert('Terjadi kesalahan saat mencari sitasi')
    } finally {
      setIsLoadingCitations(false)
      setIsProcessing(false)
      setProcessingAction('')
    }
  }

  // Handle citation selection
  const handleCitationSelect = (citation) => {
    try {
      // Check if citation already exists in map
      let citationNumber
      const citationKey = citation.url
      
      if (citationMap.has(citationKey)) {
        citationNumber = citationMap.get(citationKey)
      } else {
        // Add new citation
        const newCounter = citationCounter + 1
        setCitationCounter(newCounter)
        setCitationMap(new Map(citationMap.set(citationKey, newCounter)))
        citationNumber = newCounter
      }
      
      // Add citation number to selected text using DOM selection
      const editor = editorRef.current
      const selection = window.getSelection()
      
      if (editor && selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        const selectedTextContent = range.toString()
        
        if (selectedTextContent.trim() === selectedText.trim()) {
          // Create citation text node
          const citationText = ` [${citationNumber}]`
          const citationNode = document.createTextNode(citationText)
          
          // Move cursor to end of selection and insert citation
          range.collapse(false) // Move to end of selection
          range.insertNode(citationNode)
          
          // Clear selection
          selection.removeAllRanges()
          
          // Update content from editor
          const newContent = unprocessText(editor.innerHTML)
          handleContentChange(newContent)
          
          // Add to bibliography section
          addToBibliography(citation, citationNumber, newContent)
        } else {
          // Fallback: replace in text content
          const citationText = ` [${citationNumber}]`
          const newText = selectedText + citationText
          const updatedContent = documentContent.replace(selectedText, newText)
          handleContentChange(updatedContent)
          
          // Update editor display
          editor.innerHTML = processText(updatedContent)
          
          // Add to bibliography section
          addToBibliography(citation, citationNumber, updatedContent)
        }
      }
      
      // Close modal and sidebar
      setShowCitationModal(false)
      setShowAISidebar(false)
      setSelectedText('')
      
      // Show success notification
      setAiActionSuccess('citation')
      setTimeout(() => setAiActionSuccess(null), 3000)
    } catch (error) {
      console.error('Error adding citation:', error)
      alert('Terjadi kesalahan saat menambahkan sitasi')
    }
  }

  // Add citation to bibliography
  const addToBibliography = (citation, citationNumber, contentToUpdate = null) => {
    try {
      const url = new URL(citation.url)
      const domain = url.hostname
      const currentYear = new Date().getFullYear()
      
      // Format citation in APA style with number
      const apaFormat = `${citationNumber}. ${citation.title}. (${currentYear}). Retrieved from ${citation.url}`
      
      // Use provided content or current document content
      let currentContent = contentToUpdate || documentContent
      
      // Check if bibliography section exists
      const bibliographyRegex = /\n\n## Daftar Pustaka\/Sumber Referensi\n([\s\S]*?)(?=\n\n##|$)/
      const existingBibliography = currentContent.match(bibliographyRegex)
      
      if (existingBibliography) {
        // Check if this citation already exists
        const existingCitations = existingBibliography[1].trim()
        if (!existingCitations.includes(citation.url)) {
          // Add to existing bibliography as ordered list
          const newCitations = existingCitations ? `${existingCitations}\n${apaFormat}` : apaFormat
          currentContent = currentContent.replace(bibliographyRegex, `\n\n## Daftar Pustaka/Sumber Referensi\n\n${newCitations}`)
        }
      } else {
        // Create new bibliography section with ordered list format
        currentContent += `\n\n## Daftar Pustaka/Sumber Referensi\n\n${apaFormat}`
      }
      
      // Update content and editor
      handleContentChange(currentContent)
      
      // Update editor display
      const editor = editorRef.current
      if (editor) {
        editor.innerHTML = processText(currentContent)
      }
    } catch (error) {
      console.error('Error adding to bibliography:', error)
      // Fallback: just add the URL with number
      const fallbackFormat = `${citationNumber}. ${citation.title}. Retrieved from ${citation.url}`
      let currentContent = contentToUpdate || documentContent
      
      if (!currentContent.includes('## Daftar Pustaka/Sumber Referensi')) {
        currentContent += `\n\n## Daftar Pustaka/Sumber Referensi\n\n${fallbackFormat}`
      } else {
        currentContent += `\n${fallbackFormat}`
      }
      
      handleContentChange(currentContent)
      
      // Update editor display
      const editor = editorRef.current
      if (editor) {
        editor.innerHTML = processText(currentContent)
      }
    }
  }

  // Handle AI action
  const handleAIAction = async (action) => {
    if (!selectedText || isProcessing) return
    
    // Handle citation search separately
    if (action === 'citation') {
      await handleCitationSearch()
      return
    }
    
    setIsProcessing(true)
    setProcessingAction(action)
    
    try {
      console.log(`🔄 Starting AI action: ${action} for text: "${selectedText.substring(0, 50)}..."`)
      console.log('📋 onAIAction function:', typeof onAIAction)
      console.log('📋 Selected text length:', selectedText.length)
      console.log('📋 Document content length:', documentContent.length)
      
      const result = await onAIAction(action, selectedText, documentContent)
      
      console.log('📋 AI action result type:', typeof result)
      console.log('📋 AI action result length:', result?.length)
      console.log('📋 AI action result preview:', result?.substring(0, 100))
      
      if (result && typeof result === 'string') {
        console.log('✅ AI action completed successfully')
        // Replace selected text with AI result in contentEditable
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          range.deleteContents()
          
          // Create a document fragment with the result
          const fragment = document.createDocumentFragment()
          const div = document.createElement('div')
          div.innerHTML = processText(result)
          
          // Move all child nodes to the fragment
          while (div.firstChild) {
            fragment.appendChild(div.firstChild)
          }
          
          range.insertNode(fragment)
          
          // Update content
          const editor = editorRef.current
          if (editor) {
            const newContent = unprocessText(editor.innerHTML)
            handleContentChange(newContent)
          }
          
          // Clear selection and position cursor
          selection.removeAllRanges()
        }
        
        // Clear selection
        setSelectedText('')
        
        // Show success feedback
        setAiActionSuccess(action)
        setTimeout(() => setAiActionSuccess(null), 3000)
        
        // Keep sidebar open for a moment to show success
        setTimeout(() => setShowAISidebar(false), 1500)
      } else {
        console.error('❌ Invalid AI result:', { result, type: typeof result, length: result?.length })
        throw new Error('AI tidak mengembalikan hasil yang valid')
      }
    } catch (error) {
      console.error('❌ AI action failed:', error)
      console.error('❌ Error stack:', error.stack)
      console.error('❌ Error details:', {
        action,
        selectedTextLength: selectedText?.length,
        onAIActionType: typeof onAIAction,
        errorMessage: error.message,
        errorName: error.name
      })
      alert('Gagal melakukan aksi AI: ' + (error.message || 'Terjadi kesalahan tidak diketahui'))
    } finally {
      setIsProcessing(false)
      setProcessingAction('')
    }
  }

  // Export document
  const handleExport = (format) => {
    try {
      exportDocument(documentTitle || 'Dokumen', documentContent, format)
    } catch (error) {
      alert('Gagal mengekspor dokumen: ' + error.message)
    }
  }

  // AI Action buttons configuration
  const aiActions = [
    {
      id: 'improve',
      label: 'Perbaiki Teks',
      icon: Wand2,
      description: 'Tingkatkan kualitas dan kejelasan teks',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'expand',
      label: 'Kembangkan',
      icon: Expand,
      description: 'Perluas dengan detail dan contoh',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      id: 'shorten',
      label: 'Ringkas',
      icon: Shrink,
      description: 'Buat lebih singkat dan padat',
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      id: 'rephrase',
      label: 'Tulis Ulang',
      icon: RefreshCw,
      description: 'Ubah gaya penulisan',
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      id: 'translate',
      label: 'Terjemahkan',
      icon: Languages,
      description: 'Terjemahkan ke bahasa Inggris',
      color: 'bg-indigo-500 hover:bg-indigo-600'
    },
    {
      id: 'grammar',
      label: 'Koreksi Grammar',
      icon: CheckCircle,
      description: 'Perbaiki tata bahasa dan ejaan',
      color: 'bg-red-500 hover:bg-red-600'
    },
    {
      id: 'citation',
      label: 'Cari Sitasi',
      icon: FileText,
      description: 'Cari sumber referensi akademik untuk teks yang dipilih',
      color: 'bg-teal-500 hover:bg-teal-600'
    }
  ]

  return (
    <>
      <style>{`
        .content-editable-placeholder:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          position: absolute;
        }
        .content-editable-placeholder:focus:before {
          display: none;
        }
        
        /* Custom dropdown styling - Dark theme */
        .custom-dropdown {
          appearance: none;
          background-color: #374151;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1rem 1rem;
          border: 1px solid #4b5563;
          border-radius: 0.375rem;
          color: #e5e7eb;
          font-size: 0.75rem;
          min-width: 70px;
          padding: 0.375rem 2rem 0.375rem 0.75rem;
          transition: all 0.2s ease-in-out;
          cursor: pointer;
        }
        
        .custom-dropdown:hover {
          background-color: #4b5563;
          border-color: #6b7280;
        }
        
        .custom-dropdown:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.5);
        }
        
        /* Remove focus styling after selection */
        .custom-dropdown:not(:focus-visible) {
          border-color: #4b5563;
          box-shadow: none;
        }
        
        .custom-dropdown option {
          background-color: #374151;
          color: #e5e7eb;
        }
        
        /* Editor font size styling */
        .editor-content {
          font-size: ${fontSize}pt;
          line-height: 1.6;
        }
        
        @media (max-width: 768px) {
          .mobile-hidden {
            display: none;
          }
          .mobile-compact {
            padding: 0.5rem;
          }
          .mobile-text-sm {
            font-size: 0.875rem;
          }
          .mobile-flex-col {
            flex-direction: column;
          }
          .mobile-space-y-2 > * + * {
            margin-top: 0.5rem;
          }
          .mobile-full-width {
            width: 100% !important;
          }
          .mobile-no-margin {
            margin: 0 !important;
          }
          .mobile-padding-sm {
            padding: 0.75rem !important;
          }
        }
        
        /* Smooth transitions for mobile */
        @media (max-width: 768px) {
          .transition-transform {
            transition: transform 0.3s ease-in-out;
          }
        }
      `}</style>
    <div className="min-h-screen bg-bg-primary relative">
      {/* Main Editor Area */}
      <div className={`h-screen overflow-y-auto transition-all duration-300 ${showAISidebar ? 'md:mr-96 mr-0' : 'mr-0'}`}>
        {/* Header */}
        <div className="bg-bg-secondary border-b border-border-primary px-3 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
              <button
                onClick={onBack}
                className="flex items-center text-text-secondary hover:text-text-primary transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
                <span className="hidden sm:inline text-sm md:text-base">Kembali</span>
              </button>
              <div className="h-4 md:h-6 w-px bg-border-secondary hidden sm:block flex-shrink-0"></div>
              <FileText className="w-4 h-4 md:w-5 md:h-5 text-violet-400 flex-shrink-0" />
              <span className="text-sm md:text-lg font-semibold text-text-primary hidden sm:inline truncate">Writing Editor</span>
            </div>
            
            <div className="flex items-center space-x-1 md:space-x-3 flex-shrink-0">
              {/* Export buttons */}
              <button
                onClick={() => handleExport('txt')}
                className="flex items-center px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm bg-bg-quaternary hover:bg-state-hover rounded-lg transition-colors text-text-secondary"
              >
                <Download className="w-3 h-3 md:w-4 md:h-4 mr-0 md:mr-1" />
                <span className="hidden md:inline">TXT</span>
              </button>
              <button
                onClick={() => handleExport('md')}
                className="flex items-center px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm bg-bg-quaternary hover:bg-state-hover rounded-lg transition-colors text-text-secondary"
              >
                <Download className="w-3 h-3 md:w-4 md:h-4 mr-0 md:mr-1" />
                <span className="hidden md:inline">MD</span>
              </button>
            </div>
          </div>
        </div>

        {/* Document Title */}
        <div className="bg-bg-secondary border-b border-border-primary px-3 md:px-6 py-3 md:py-4">
          <input
            type="text"
            value={documentTitle}
            onChange={handleTitleChange}
            placeholder="Judul Dokumen"
            className="w-full text-lg md:text-2xl font-bold text-text-primary border-none outline-none bg-transparent placeholder-text-placeholder"
          />
        </div>

        {/* Simple Formatting Toolbar */}
        <div className="bg-bg-tertiary border-b border-border-primary px-3 md:px-6 py-2 md:py-3 sticky top-0 z-10">
          <div className="flex items-center justify-between space-x-2 md:space-x-4">
            {/* Left side - Formatting tools */}
            <div className="flex items-center space-x-1 md:space-x-2 overflow-x-auto flex-1 min-w-0">
              {/* Undo/Redo */}
              <div className="flex items-center space-x-0.5 flex-shrink-0">
                <button
                  onClick={handleUndo}
                  disabled={htmlUndoStack.length <= 1}
                  title="Undo (Ctrl+Z)"
                  className="p-1.5 rounded hover:bg-bg-quaternary text-text-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Undo className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={htmlRedoStack.length === 0 || redoStack.length === 0}
                  title="Redo (Ctrl+Y)"
                  className="p-1.5 rounded hover:bg-bg-quaternary text-text-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Redo className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Separator */}
              <div className="h-4 w-px bg-border-secondary flex-shrink-0"></div>
              
              {/* Font Size Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                    className="custom-dropdown"
                    value={fontSize}
                    onChange={(e) => {
                      const newSize = parseInt(e.target.value)
                      // Try to apply font size to selected text first
                      const appliedToSelection = applyFontSizeToSelection(newSize)
                      
                      // If no text was selected, change the default font size for new text
                      if (!appliedToSelection) {
                        handleFontSizeChange(newSize)
                      }
                      
                      e.target.blur() // Remove focus after selection
                    }}
                  >
                    {[6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 32, 36, 48, 60, 72].map(size => (
                      <option key={size} value={size}>{size}pt</option>
                    ))}
                  </select>
              </div>
              
              {/* Heading Selector Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={selectedHeading}
                  onChange={(e) => {
                    applyHeading(e.target.value)
                    e.target.blur() // Remove focus after selection
                  }}
                  className="custom-dropdown"
                >
                  <option value="normal">Normal</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                  <option value="h4">Heading 4</option>
                  <option value="h5">Heading 5</option>
                  <option value="h6">Heading 6</option>
                </select>
              </div>
              
              {/* Separator */}
              <div className="h-4 w-px bg-border-secondary flex-shrink-0"></div>
              
              {/* Basic Text Formatting */}
              <div className="flex items-center space-x-0.5 md:space-x-1 flex-shrink-0">
                <button
                  onClick={() => applyFormatting('bold')}
                  title="Bold (Ctrl+B)"
                  className="p-1.5 rounded hover:bg-bg-quaternary text-text-secondary transition-colors"
                >
                  <Bold className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => applyFormatting('italic')}
                  title="Italic (Ctrl+I)"
                  className="p-1.5 rounded hover:bg-bg-quaternary text-text-secondary transition-colors"
                >
                  <Italic className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => applyFormatting('underline')}
                  title="Underline (Ctrl+U)"
                  className="p-1.5 rounded hover:bg-bg-quaternary text-text-secondary transition-colors"
                >
                  <Underline className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Separator */}
              <div className="h-4 w-px bg-border-secondary flex-shrink-0"></div>
              
              {/* Text Alignment */}
              <div className="flex items-center space-x-0.5 md:space-x-1 flex-shrink-0">
                <button
                  onClick={() => setTextAlign('left')}
                  title="Align Left"
                  className={`p-1.5 rounded transition-colors ${
                    textAlign === 'left' ? 'bg-violet-500/20 text-violet-400' : 'hover:bg-bg-quaternary text-text-secondary'
                  }`}
                >
                  <AlignLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setTextAlign('center')}
                  title="Align Center"
                  className={`p-1.5 rounded transition-colors ${
                    textAlign === 'center' ? 'bg-violet-500/20 text-violet-400' : 'hover:bg-bg-quaternary text-text-secondary'
                  }`}
                >
                  <AlignCenter className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setTextAlign('right')}
                  title="Align Right"
                  className={`p-1.5 rounded transition-colors ${
                    textAlign === 'right' ? 'bg-violet-500/20 text-violet-400' : 'hover:bg-bg-quaternary text-text-secondary'
                  }`}
                >
                  <AlignRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            
            {/* Right side - AI Tools Toggle */}
            <button
              onClick={() => setShowAISidebar(!showAISidebar)}
              className={`flex items-center px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm rounded-lg transition-colors flex-shrink-0 ${
                showAISidebar 
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-400/30' 
                  : 'bg-bg-quaternary hover:bg-state-hover text-text-secondary border border-border-secondary'
              }`}
              title="AI Tools (Ctrl+Shift+A)"
            >
              <Wand2 className="w-3.5 h-3.5 md:w-4 md:h-4 md:mr-2" />
              <span className="hidden md:inline">AI Tools</span>
            </button>
          </div>
          
          {/* Second row - Document Stats and Save */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border-secondary">
            {/* Document Stats */}
            <div className="text-xs md:text-sm text-text-tertiary flex flex-wrap gap-2 md:space-x-4">
              <span className="whitespace-nowrap">{documentStats.words} kata</span>
              <span className="whitespace-nowrap hidden sm:inline">{documentStats.characters} karakter</span>
              <span className="whitespace-nowrap hidden md:inline">{documentStats.readingTime} menit baca</span>
              <span className="whitespace-nowrap hidden lg:inline">Halaman {documentStats.pages}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Auto-save indicator */}
              {lastSaved && (
                <div className="flex items-center text-xs md:text-sm text-green-400">
                  <Save className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                  <span className="hidden sm:inline">Tersimpan {lastSaved.toLocaleTimeString()}</span>
                  <span className="sm:hidden">Tersimpan</span>
                </div>
              )}
              
              {/* Manual Save Button */}
              <button
                onClick={handleManualSave}
                disabled={isSaving}
                className="flex items-center px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white rounded transition-colors"
                title="Simpan dokumen secara manual"
              >
                {isSaving ? (
                  <Loader2 className="w-3 h-3 md:w-4 md:h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                )}
                <span className="hidden sm:inline">{isSaving ? 'Menyimpan...' : 'Simpan'}</span>
                <span className="sm:hidden">{isSaving ? '...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>



        {/* Editor Container - Simple Single Page */}
        <div className="p-3 md:p-6 bg-bg-primary min-h-screen">
          <div className="max-w-4xl mx-auto">
            {/* A4 Paper Simulation - Responsive */}
            <div
              className="bg-white shadow-lg border border-border-secondary mx-auto" 
              style={{ 
                width: window.innerWidth < 768 ? '100%' : '210mm', 
                minHeight: window.innerWidth < 768 ? 'calc(100vh - 200px)' : '297mm', 
                margin: '0 auto', 
                overflow: 'visible' 
              }}
            >
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning={true}
                onInput={handleEditorInput}
                onMouseUp={handleTextSelection}
                onKeyUp={handleTextSelection}
                data-placeholder="Mulai menulis..."
                className="w-full border-none outline-none bg-transparent min-h-full resize-none content-editable-placeholder editor-content"
                style={{
                  fontFamily: fontFamily === 'Georgia' ? 'Georgia, serif' : fontFamily,
                  fontSize: window.innerWidth < 768 ? `${Math.max(fontSize - 2, 10)}pt` : `${fontSize}pt`,
                  lineHeight: '1.6',
                  textAlign: textAlign,
                  color: '#000000',
                  minHeight: window.innerWidth < 768 ? 'calc(100vh - 200px)' : '297mm',
                  padding: window.innerWidth < 768 ? '1rem' : '25.4mm 20mm 25.4mm 20mm',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {showAISidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setShowAISidebar(false)}
        />
      )}
      
      {/* AI Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-full md:w-96 border-l border-gray-700 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
        showAISidebar ? 'translate-x-0' : 'translate-x-full'
      }`} style={{backgroundColor: '#232323'}}>
          <div className="p-5 h-full overflow-y-auto" style={{backgroundColor: '#232323'}}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Wand2 className="w-3.5 h-3.5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">AI Tools</h3>
              </div>
              <button
                onClick={() => setShowAISidebar(false)}
                className="text-gray-400 hover:text-white transition-colors p-1.5 hover:bg-gray-800 rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {/* Selected Text Preview */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Teks yang dipilih
              </label>
              <div className="p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm text-gray-300 max-h-24 overflow-y-auto">
                {selectedText ? (
                  <div className="whitespace-pre-wrap">{selectedText}</div>
                ) : (
                  <div className="text-gray-500 italic flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-gray-600 rounded-full animate-pulse"></div>
                    <span>Pilih teks di editor untuk menggunakan AI tools</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Instructions */}
            {!selectedText && (
              <div className="mb-5 p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-blue-300 mb-1.5">
                      Cara menggunakan AI Tools:
                    </h4>
                    <ol className="text-xs text-blue-200 space-y-1">
                      <li className="flex items-center space-x-1.5">
                        <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                        <span>Pilih/highlight teks yang ingin diedit</span>
                      </li>
                      <li className="flex items-center space-x-1.5">
                        <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                        <span>Pilih aksi AI yang diinginkan</span>
                      </li>
                      <li className="flex items-center space-x-1.5">
                        <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                        <span>Tunggu hasil AI menggantikan teks</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
            
            {/* AI Actions */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {selectedText ? 'Pilih aksi AI:' : 'Aksi AI tersedia:'}
              </label>
              
              {aiActions.map((action) => {
                const Icon = action.icon
                const isCurrentlyProcessing = isProcessing && processingAction === action.id
                const isDisabled = !selectedText || isProcessing
                
                return (
                  <button
                    key={action.id}
                    onClick={() => handleAIAction(action.id)}
                    disabled={isDisabled}
                    className={`w-full group relative overflow-hidden rounded-lg transition-all duration-200 ${
                      isDisabled
                        ? 'bg-gray-800 border border-gray-700 cursor-not-allowed opacity-50' 
                        : 'bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-gray-600 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center p-3 relative z-10">
                      {isCurrentlyProcessing ? (
                        <div className="w-4 h-4 mr-3 relative">
                          <div className="w-4 h-4 border-2 border-violet-500/20 border-t-violet-500 rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        <div className="w-4 h-4 mr-3 relative">
                          <Icon className="w-4 h-4 text-violet-400" />
                        </div>
                      )}
                      <div className="text-left flex-1">
                        <div className="text-sm font-medium text-white">{action.label}</div>
                        <div className="text-xs text-gray-400 hidden md:block mt-0.5">{action.description}</div>
                      </div>
                      {!isDisabled && (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-500 group-hover:text-gray-300 transition-colors" />
                      )}
                    </div>
                    {!isDisabled && (
                      <div className="absolute inset-0 bg-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    )}
                  </button>
                )
              })}
            </div>
            
            {/* Success Notification */}
            {aiActionSuccess && (
              <div className="mt-4 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
                <div className="flex items-center text-green-300">
                  <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                  <span className="text-sm font-medium">
                    Aksi "{aiActions.find(a => a.id === aiActionSuccess)?.label}" berhasil!
                  </span>
                </div>
              </div>
            )}
            
            {/* Keyboard Shortcuts Info - Hidden on Mobile */}
            <div className="mt-5 p-3 bg-gray-800 border border-gray-700 rounded-lg hidden md:block">
              <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center">
                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full mr-2"></span>
                Keyboard Shortcuts
              </h4>
              <div className="text-xs text-gray-400 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span>Toggle AI Tools</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+Shift+A</kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Close AI Tools</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Esc</kbd>
                </div>
                <div className="h-px bg-gray-700 my-1.5"></div>
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span>Undo</span>
                    <kbd className="px-1 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+Z</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Redo</span>
                    <kbd className="px-1 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+Y</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Bold</span>
                    <kbd className="px-1 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+B</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Italic</span>
                    <kbd className="px-1 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+I</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Copy</span>
                    <kbd className="px-1 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+C</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Paste</span>
                    <kbd className="px-1 py-0.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-300">Ctrl+V</kbd>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Credit Info */}
            <div className="mt-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg">
              <div className="flex items-center text-amber-300">
                <AlertCircle className="w-4 h-4 mr-2 text-amber-400" />
                <span className="text-sm">Setiap aksi AI menggunakan 1 kredit</span>
              </div>
            </div>
          </div>
        </div>
      
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-secondary border border-border-primary p-6 rounded-xl shadow-2xl flex flex-col items-center space-y-4 max-w-sm w-full">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-violet-400/40 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-text-primary mb-1">
                {processingAction === 'improve' && 'Memperbaiki Teks...'}
                {processingAction === 'expand' && 'Mengembangkan Teks...'}
                {processingAction === 'shorten' && 'Meringkas Teks...'}
                {processingAction === 'rephrase' && 'Menulis Ulang...'}
                {processingAction === 'translate' && 'Menerjemahkan...'}
                {processingAction === 'grammar' && 'Memperbaiki Grammar...'}
                {processingAction === 'citation' && 'Mencari Sitasi...'}
                {!processingAction && 'Memproses dengan AI...'}
              </div>
              <div className="text-sm text-text-secondary">
                Mohon tunggu sebentar
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Citation Modal */}
      {showCitationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-secondary border border-border-primary rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-border-secondary bg-bg-tertiary">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">Hasil Pencarian Sitasi</h3>
                    <p className="text-sm text-text-secondary mt-1">Untuk teks: "{citationQuery}"</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCitationModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-quaternary transition-all duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] bg-bg-secondary">
              {isLoadingCitations ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative mb-4">
                    <div className="w-10 h-10 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 w-10 h-10 border-4 border-transparent border-r-violet-400/40 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
                  </div>
                  <span className="text-text-primary font-medium">Mencari sumber referensi...</span>
                  <span className="text-text-secondary text-sm mt-1">Mohon tunggu sebentar</span>
                </div>
              ) : citationResults.length > 0 ? (
                <div className="space-y-4">
                  {citationResults.map((citation, index) => (
                    <div key={index} className="group border border-border-secondary rounded-xl p-5 hover:border-violet-500/30 hover:bg-bg-tertiary/50 transition-all duration-200">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-8 h-8 bg-violet-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-violet-400 font-semibold text-sm">{index + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-text-primary mb-2 line-clamp-2 group-hover:text-violet-300 transition-colors">{citation.title}</h4>
                              <p className="text-sm text-text-secondary mb-3 line-clamp-3 leading-relaxed">{citation.snippet}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-text-tertiary pl-11">
                            <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 bg-violet-400 rounded-full"></div>
                              <span className="font-medium">{new URL(citation.url).hostname}</span>
                            </div>
                            <span className="text-border-secondary">•</span>
                            <span className="truncate max-w-xs">{citation.url}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleCitationSelect(citation)}
                          className="flex-shrink-0 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-violet-500/25 active:scale-95"
                        >
                          Gunakan Sitasi
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-bg-tertiary rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-text-tertiary" />
                  </div>
                  <h4 className="text-text-primary font-medium mb-2">Tidak Ada Hasil</h4>
                  <p className="text-text-secondary text-sm">Tidak ditemukan sumber referensi yang relevan untuk teks yang dipilih</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-border-secondary bg-bg-tertiary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                  <p className="text-sm text-text-secondary">
                    Ditemukan <span className="font-medium text-text-primary">{citationResults.length}</span> sumber referensi
                  </p>
                </div>
                <button
                  onClick={() => setShowCitationModal(false)}
                  className="px-4 py-2 text-text-secondary hover:text-text-primary hover:bg-bg-quaternary rounded-lg transition-all duration-200"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}

WritingEditorPage.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  onTitleUpdate: PropTypes.func.isRequired,
  onContentUpdate: PropTypes.func.isRequired,
  onAIAction: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired,
  documentId: PropTypes.string,
  onSave: PropTypes.func
}

export default WritingEditorPage